import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';

import { cn } from '@/lib/utils';

import { GROUP_WEEKDAY_KEYS } from '@/constants';

type WeekdayPickerProps = {
  weekdays: readonly number[];
  value: number[];
  error?: string;
  onToggle: (weekday: number) => void;
};

export const WeekdayPicker = (props: WeekdayPickerProps) => {
  const { weekdays, value, error, onToggle } = props;
  const { t } = useTranslation();

  const label = t('Weekdays');

  return (
    <div className="grid gap-2">
      <div className="relative">
        <fieldset
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute inset-0 top-[-5px] overflow-hidden rounded-md border-2 px-[9px]',
            error ? 'border-destructive' : 'border-input',
          )}
        >
          <legend className="invisible block h-[11px] w-auto max-w-full overflow-hidden p-0 text-xs whitespace-nowrap">
            <span className="visible inline-block px-1 opacity-0">{label}</span>
          </legend>
        </fieldset>
        <span
          className={cn(
            'pointer-events-none absolute top-0 left-[13px] -translate-y-1/2 text-xs',
            error ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          {label}
        </span>

        <div className="flex flex-wrap gap-2 p-3">
          {weekdays.map((weekday) => {
            const isPicked = value.includes(weekday);

            return (
              <Button
                key={weekday}
                type="button"
                size="sm"
                variant={isPicked ? 'default' : 'outline'}
                className="min-w-16"
                aria-pressed={isPicked}
                onClick={() => onToggle(weekday)}
              >
                {t(GROUP_WEEKDAY_KEYS[weekday])}
              </Button>
            );
          })}
        </div>
      </div>

      {!!error && <p className="text-destructive text-sm">{error}</p>}
    </div>
  );
};
